import MagnifyingGlass from '@mds/icons/MagnifyingGlass';
import X from '@mds/icons/X';
import { IconButton, InputAdornment, TextField, TextFieldProps, useForkRef } from '@mui/material';
import { ChangeEvent, forwardRef, useCallback, useRef, useState } from 'react';
import { inputAdornmentIconSizes, inputClearIconSizes } from '../../theme/styleOverrides/utils';

export type SearchInputProps = TextFieldProps;

/**
 * Text field with a search icon and a clear button
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState('');
 * <SearchInput defaultValue={value} onChange={(event) => setValue(event.target.value)} />
 * ```
 *
 * Using an uncontrolled input may have better performance characteristics - keeping state
 * in DOM means that keystrokes will not be blocked by React's render cycle. Alternatively,
 * consider using `useDeferredValue()` to deprioritize updates caused by the onChange callback.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(props, parentRef) {
  const ownInputRef = useRef<HTMLInputElement>(null);
  const forkInputRef = useForkRef(props.inputRef, ownInputRef);
  const [hasValue, setHasValue] = useState(!!props.defaultValue);
  const { onChange } = props;
  const size = props.size ?? 'medium';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHasValue(!!event.target.value);
      onChange?.(event);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    if (!ownInputRef.current) {
      return;
    }
    // Preserve uncontrolled field to retain native input speed
    ownInputRef.current.value = '';
    setHasValue(false);

    onChange?.({ target: { value: '' } } as any);
    ownInputRef.current.focus();
  }, [onChange]);

  return (
    <TextField
      hiddenLabel
      placeholder="Search"
      autoComplete="off"
      {...props}
      ref={parentRef}
      inputRef={forkInputRef}
      onChange={handleChange}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <MagnifyingGlass
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                  fontSize: inputAdornmentIconSizes[size],
                  height: inputAdornmentIconSizes[size],
                  width: inputAdornmentIconSizes[size],
                }}
              />
            </InputAdornment>
          ),
          endAdornment: hasValue ? (
            <InputAdornment position="end">
              <IconButton onClick={handleClear} aria-label="Clear input" sx={{ mr: '-4px' }}>
                <X
                  className="MdsInputClearIcon"
                  fontSize="small"
                  sx={{
                    color: 'text.secondary',
                    fontSize: inputClearIconSizes[size],
                    height: inputClearIconSizes[size],
                    width: inputClearIconSizes[size],
                  }}
                />
              </IconButton>
            </InputAdornment>
          ) : null,
          ...props.slotProps?.input,
        },
        ...props.slotProps,
      }}
    />
  );
});
